import { EMPTY, from, Observable, of, pipe, ReplaySubject, throwError } from 'rxjs';
import { finalize, map, share, switchMap } from 'rxjs/operators';

let sessionToken = localStorage.getItem('gpt-session-token');
if (!sessionToken) {
    gotoLogin();
}

export let userInfo$ = of(undefined).pipe(
    switchMap(() => from(authorizedFetch('/api/userinfo'))),
    readJson(),
    share({
        connector: () => new ReplaySubject(1),
        resetOnError: true,
        resetOnComplete: false,
        resetOnRefCountZero: false,
    }),
);

export let defaultLang = 'en';
let supportedLanguages = new Set(['de', 'en']);

export let lang$ = userInfo$.pipe(
    map(info => (info.oauth.locale || defaultLang).substring(0, 2)),
    map(lang => supportedLanguages.has(lang) ? lang : defaultLang),
);

export function fetchText(path, opts={}) {
    return of(undefined).pipe(
        switchMap(() => from(fetch(path, mergeAuthorizationHeader(opts)))),
        validateResponse(),
        switchMap(res => from(res.text())),
    );
}

export function postText(path, text, opts={}) {
    return of(undefined).pipe(
        switchMap(() => from(fetch(path, mergeAuthorizationHeader({
            ...opts,
            method: 'POST',
            headers: {
                ...opts.header,
                'Accept': 'application/json',
                'Content-Type': 'text/plain; encoding=utf-8',
            },
            body: new TextEncoder().encode(text),
        })))),
        validateResponse(),
        switchMap(res => from(res.json())),
    );
}

export function postJson(path, body, opts={}) {
    return of(undefined).pipe(
        switchMap(() => from(fetch(path, mergeAuthorizationHeader({
            ...opts,
            method: 'POST',
            headers: {
                ...opts.header,
                'Accept': 'application/json',
                'Content-Type': 'application/json; encoding=utf-8',
            },
            body: new TextEncoder().encode(JSON.stringify(body)),
        })))),
        validateResponse(),
        switchMap(res => from(res.json())),
    );
}

export function deleteEntity(path) {
    return of(undefined).pipe(
        switchMap(() => from(fetch(path, mergeAuthorizationHeader({
            method: 'DELETE',
        })))),
        validateResponse(),
    );
}

export function authorizedFetch(path, opts={}) {
    return fetch(path, mergeAuthorizationHeader(opts));
}

function mergeAuthorizationHeader(opts={}) {
    return {
        ...opts,
        headers: {
            ...opts.headers || {},
            'Authorization': `Bearer ${sessionToken}`,
        },
    };
}

export function readJson() {
    return pipe(
        validateResponse(),
        switchMap(res => from(res.json())),
    );
}

export function validateResponse() {
    return switchMap(res => {
        if (res.status === 401) {
            forgetSession();
            gotoLogin();
            return EMPTY;
        }
        if (!res.ok) {
            return throwError(res);
        }
        return of(res);
    });
}

export function logout() {
    return of(undefined).pipe(
        switchMap(() => from(authorizedFetch('/api/logout', {
            method: 'POST',
            data: '',
        }))),
        validateResponse(),
        finalize(() => {
            // no matter if the logout was a success or not… drop the
            // session token and navigate to login
            forgetSession();
            gotoLogin();
        }),
    );
}

function forgetSession() {
    localStorage.removeItem('gpt-session-token');
}

function gotoLogin() {
    let proto = window.location.protocol;
    let host = window.location.host;
    window.location = `https://auth.ma300k.de/authorize?response_type=code&client_id=fvaEPJZ466ZuWWCdgNHIeLorLzTeSqvZ&scope=openid%20profile%20email&redirect_uri=${proto}//${host}/auth-grant.html`;
}

export function fetchNdJson(path, opts={}) {
    return new Observable(observer => {
        let ctrl = new AbortController();
        authorizedFetch(path, {
            ...opts,
            signal: ctrl.signal,
        })
            .then(res => {
                if (!res.ok) {
                    observer.error(res);
                    return;
                }
                let rd = res.body.getReader();
                let sep = /\r?\n/;
                let decoder = new TextDecoder();
                let buf = '';

                readNext();

                function readNext() {
                    rd.read()
                        .then(({ done, value }) => {
                            if (done) {
                                if (buf.byteLength > 0) {
                                    observer.error('Unable to drain buffer');
                                    return;
                                }
                                observer.complete();
                                return;
                            }
                            buf += decoder.decode(value, { stream: true });
                            let messages = buf.split(sep);
                            for (let i = 0; i < messages.length - 1; ++i) {
                                let msg = JSON.parse(messages[i]);
                                observer.next(msg);
                                if (msg.done) {
                                    return;
                                }
                            }
                            buf = messages[messages.length - 1];
                            readNext();
                        })
                        .catch(err => observer.error(err));
                }
            })
            .catch(err => observer.error(err));

        return () => ctrl.abort();
    });
}
